import { api } from '../api';
import { useLoad, usePolling } from '../hooks';
import { itemCount, stamp, timeAgo } from '../format';
import { Card, Empty, ErrorNote, Loading, Metric, StatusPill, Table } from '../components/ui';
import { useNavigate } from '../router';
import type { PlatformHealth, Page, ResourceSummary, RunSummary } from '../types';

/**
 * Operatörün açık bıraktığı tek ekran.
 *
 * Üç soruyu cevaplar, fazlasını değil: platform ayakta mı, kimlik havuzu taramayı
 * sürdürecek kadar sağlıklı mı, ve az önce bir şey patladı mı.
 */
export function Dashboard() {
  const navigate = useNavigate();
  const health = useLoad(() => api.get<PlatformHealth>('/health'));
  const resources = useLoad(() => api.get<Page<ResourceSummary>>('/v1/resources?limit=100'));
  const runs = useLoad(() => api.get<Page<RunSummary>>('/v1/runs?limit=10'));

  usePolling(health.reload, 15_000);
  usePolling(resources.reload, 15_000);
  usePolling(runs.reload, 10_000);

  const all = resources.data?.items ?? [];
  const live = all.filter((r) => !r.deactivatedAt);
  const identities = live.filter((r) => r.type === 'IDENTITY');
  const proxies = live.filter((r) => r.type === 'PROXY');
  const usable = (list: ResourceSummary[]) =>
    list.filter((r) => r.status === 'ACTIVE' || r.status === 'DEGRADED').length;
  const attention = live.filter((r) => r.status === 'CHALLENGED' || r.status === 'DEAD');
  const recentRuns = runs.data?.items ?? [];
  const failed = recentRuns.filter((r) => r.status === 'FAILED' || r.status === 'TIMED_OUT');
  const checks = Object.entries(health.data?.checks ?? {});

  return (
    <>
      {identities.length === 0 && !resources.loading && (
        <div className="banner banner-warn">
          Havuzda hiç Instagram kimliği yok — hiçbir tarama çalışamaz.{' '}
          <button type="button" className="linky" onClick={() => navigate('/instagram-accounts')}>
            Hesap ekle
          </button>
        </div>
      )}

      <div className="metrics">
        <Metric
          label="Kullanılabilir kimlik"
          value={`${usable(identities)}/${identities.length}`}
          tone={usable(identities) === 0 ? 'bad' : undefined}
          hint="aktif ya da sorunlu"
        />
        <Metric
          label="Kullanılabilir proxy"
          value={`${usable(proxies)}/${proxies.length}`}
          hint={proxies.length === 0 ? 'doğrudan çıkış' : 'erişilebilir'}
        />
        <Metric
          label="Dinlenen kimlik"
          value={identities.filter((r) => r.status === 'COOLDOWN').length}
          hint="hız sınırından sonra"
        />
        <Metric
          label="İlgi bekleyen"
          value={attention.length}
          tone={attention.length > 0 ? 'bad' : 'good'}
          hint={attention.length > 0 ? 'operatör müdahalesi' : 'her şey yolunda'}
        />
      </div>

      <Card title="Platform">
        <ErrorNote message={health.error} />
        {health.loading && !health.data && <Loading />}
        {health.data && (
          <div className="metrics">
            <Metric
              label="API"
              value={health.data.status === 'ok' ? 'Ayakta' : 'Sorunlu'}
              tone={health.data.status === 'ok' ? 'good' : 'bad'}
              hint={`sürüm ${health.data.version}`}
            />
            <Metric label="Çalışma süresi" value={`${Math.round(health.data.uptimeSec / 60)} dk`} />
            {checks.map(([name, check]) => (
              <Metric
                key={name}
                label={name === 'database' ? 'Veritabanı' : name === 'redis' ? 'Redis' : name}
                value={check.status === 'ok' ? 'Ayakta' : 'Sorunlu'}
                tone={check.status === 'ok' ? 'good' : 'bad'}
                hint={check.latencyMs ? `${check.latencyMs} ms` : check.error}
              />
            ))}
          </div>
        )}
      </Card>

      {attention.length > 0 && (
        <Card title="İlgi bekleyenler" flush>
          <Table head={['Kaynak', 'Tür', 'Durum', 'Son hata', 'Ne zaman']}>
            {attention.map((resource) => (
              <tr
                key={resource.id}
                className="clickable"
                onClick={() =>
                  navigate(resource.type === 'IDENTITY' ? '/instagram-accounts' : '/proxies')
                }
              >
                <td className="cell-title">{resource.name}</td>
                <td className="muted">{resource.type === 'IDENTITY' ? 'Kimlik' : 'Proxy'}</td>
                <td>
                  <StatusPill status={resource.status} />
                </td>
                <td className="muted mono">{resource.health.lastErrorCode ?? '—'}</td>
                <td className="muted">{timeAgo(resource.health.lastFailureAt)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      <Card
        title="Son çalışmalar"
        flush
        actions={
          <button type="button" className="ghost tiny" onClick={() => navigate('/runs')}>
            Tümü
          </button>
        }
      >
        <ErrorNote message={runs.error} />
        {recentRuns.length === 0 && !runs.loading && (
          <Empty title="Henüz çalışma yok">Bir actor çalıştırıldığında burada görünür.</Empty>
        )}
        {recentRuns.length > 0 && (
          <>
            {failed.length > 0 && (
              <div className="banner banner-bad" style={{ margin: '12px 16px 0' }}>
                Son {recentRuns.length} çalışmanın {failed.length} tanesi başarısız.
              </div>
            )}
            <Table head={['Çalışma', 'Actor', 'Durum', 'Öğe', 'Başlangıç']}>
              {recentRuns.map((run) => (
                <tr key={run.id} className="clickable" onClick={() => navigate(`/runs/${run.id}`)}>
                  <td className="mono">{run.id.slice(0, 12)}…</td>
                  <td className="cell-title">{run.actorSlug}</td>
                  <td>
                    <StatusPill status={run.status} />
                  </td>
                  <td>{itemCount(run.stats)}</td>
                  <td className="muted" title={stamp(run.startedAt)}>
                    {timeAgo(run.startedAt ?? run.createdAt)}
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Card>
    </>
  );
}
